import { useId } from "react";
import { classNames } from "../../helpers/classNames";

/**
 * Text input with label, helper text, error and an optional counter
 * (docs/design/HANDOFF.md, "Typography": inputs never under 16px so iOS
 * doesn't zoom; borders from the "Colors" table).
 *
 * `error` turns the border red and is announced; `helper` sits under the
 * field in muted text. With `maxLength` and `showCount`, a "21/40" counter
 * appears on the right.
 */
const TextField = ({
  id,
  label,
  helper,
  error,
  value,
  maxLength,
  showCount = false,
  multiline = false,
  rows = 5,
  className,
  inputClassName,
  ...inputProps
}) => {
  const autoId = useId();
  const fieldId = id || `field-${autoId}`;
  const helperId = helper ? `${fieldId}-help` : undefined;
  const errorId = error ? `${fieldId}-error` : undefined;
  const Tag = multiline ? "textarea" : "input";

  return (
    <div className={classNames("flex flex-col gap-2", className)}>
      {label && (
        <label htmlFor={fieldId} className="text-sm md:text-[15px] font-semibold text-md-dark">
          {label}
        </label>
      )}
      <Tag
        id={fieldId}
        value={value}
        maxLength={maxLength}
        rows={multiline ? rows : undefined}
        aria-invalid={error ? "true" : undefined}
        aria-describedby={[errorId, helperId].filter(Boolean).join(" ") || undefined}
        className={classNames(
          "w-full rounded-xl bg-white px-4 text-base md:text-[17px] font-medium text-md-dark placeholder:text-md-muted/70",
          "border-2 outline-none transition-colors duration-150 focus:border-md-pink !ring-0",
          multiline ? "py-3 min-h-[120px] resize-none" : "h-[54px]",
          error ? "border-red-700" : "border-md-control-input",
          inputClassName
        )}
        {...inputProps}
      />
      {(helper || error || (showCount && maxLength)) && (
        <div className="flex items-start justify-between gap-3 text-[13px] leading-snug">
          <div className="min-w-0">
            {error ? (
              <p id={errorId} role="alert" className="text-red-900">
                {error}
              </p>
            ) : helper ? (
              <p id={helperId} className="text-md-muted">
                {helper}
              </p>
            ) : null}
          </div>
          {showCount && maxLength ? (
            <span className="flex-shrink-0 text-md-muted" aria-hidden="true">
              {String(value ?? "").length}/{maxLength}
            </span>
          ) : null}
        </div>
      )}
    </div>
  );
};

export default TextField;
